import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import pluralize from 'pluralize';

import { dateService } from '../../../services/date';
import { api } from '../../../services/api';
import { session } from '../../../services/session';
import { STRATEGY_OBJECTIVES } from '../../../utils/constants';
import { buildCountDescription, buildQueryFilter } from '../../../utils/utils';
import { formatCellValue, formatNumber } from '../../base/common/utils';

import Button from '../../ui/button';
import Search from '../../ui/search/search';
import DropdownSelect from '../../ui/dropdown';
import { Loader, NoResult } from '../../misc/loader-utils';
import { Table } from '../../ui/table';
import Column from '../../ui/table/column';
import DropdownToggleWrapper from '../../ui/dropdown/dropdown-toggle-wrapper';
import StrategyStatus from '../../ui/strategy-status';
import PacingStatus from '../../ui/pacing-status/pacing-status';
import Pagination from '../../base/common/pagination';
import DatePicker from '../../base/date/date-picker';
import ReportDropdown from '../../base/dropdowns/report-dropdown';
import PublisherDropdown from '../../base/dropdowns/v2/publisher-dropdown';
import MerchantStrategyTotals from '../campaign-tracking/merchant-strategy-totals';

import ModalWrapper from '../../base/modals/modal-wrapper';
import InputWrapper from '../../ui/form-elements/form-page/input-wrapper';
import SnackBar from '../../admin/accounts/snackbar';

import './campaign-center.scss';

const defaultDateParams = {
  startDate: dateService.getStartOf(null, 'month'),
  endDate: dateService.getCurrentDateTime(),
};

const statusDropdownItems = [
  { label: 'Completed', value: 3 },
  { label: 'Upcoming', value: 2 },
  { label: 'Paused', value: 4 },
  { label: 'Live', value: 1 },
];

// hidden_states utilizes STRATEGY_STATUSES from constants
const strategyStatusItems = [
  { label: 'Edit', value: 'edit', visible_states: [true, false] },
  { label: 'Pause', value: 'pause', visible_states: [true] },
  { label: 'Unpause', value: 'unpause', visible_states: [false] },
];

const statType = 'campaign-center-strategies-tab';

const StrategiesTab = props => {
  const { strategyGroupFilterId, topBarData, merchantId } = props;
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState([]);
  const [labelFilter, setLabelFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCampaignSelectOpen, setCampaignSelectOpen] = useState(false);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [newStrategyCampaign, setNewStrategyCampaign] = useState();
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedPubs, setSelectedPubs] = useState([]);
  const [updatedCampaignStatus, setUpdatedCampaignStatus] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState([]);
  const [snackbarTxt, setSnackbarTxt] = useState('');
  // TODO: Do we want the date-range to persist between tabs?
  const [dateRange, setDateRange] = useState(defaultDateParams);
  const history = useHistory();

  const roiActualRender = (cellData, rowData) => {
    // TODO: Move this into a generic wrapper function
    if (typeof cellData === 'undefined' || cellData === null) {
      return '-';
    }

    const cellValue = formatCellValue(rowData.roi, null, { precision: 1 });

    if (rowData.strategy_pricing_model === 'CPC') {
      return cellValue;
    }

    const pacingIndicator = PacingStatus({
      value: rowData.strategy_roi_goal
        ? parseFloat(rowData.roi) / parseFloat(rowData.strategy_roi_goal)
        : 0,
      size: 11,
      type: 'roi',
    });

    return (
      <div className="pacing-cell">
        <div style={{ color: pacingIndicator.props.color }}>{cellValue}</div>
        {pacingIndicator}
      </div>
    );
  };

  const columns = [
    {
      dataKey: 'strategy_id',
      flexWidth: true,
      width: 50,
      cellRender: ({ cellData, rowData, rowIndex }) => {
        const strategyIsEnabled = rowData.strategy_is_enabled;
        const dropdownItems = strategyStatusItems.filter(item => {
          return item.visible_states.includes(strategyIsEnabled);
        });

        let menuAlign = 'left';

        if (perPage - rowIndex <= 2) {
          menuAlign += 'top';
        }

        return (
          <DropdownToggleWrapper
            items={dropdownItems}
            onSelect={option => {
              setSnackbarTxt(`Updated status for strategy "${rowData.strategy_name}"`);
              onOverflowSelect(option.value, rowData.strategy_group_id, cellData);
            }}
            menuAlign={menuAlign}
          >
            <Button kind="text" iconOnly="more" />
          </DropdownToggleWrapper>
        );
      },
    },
    {
      dataKey: 'strategy_status',
      label: 'Status',
      cellRender: ({ cellData }) => (
        <StrategyStatus status={cellData} enumKeys={true} styleType="normal" />
      ),
    },
    {
      dataKey: 'strategy_name',
      label: 'Campaign',
      width: 350,
      flexWidth: true,
      cellRender: ({ cellData, rowData }) => (
        <div style={{ whiteSpace: 'normal' }}>
          <Link to={`/v2/merchant/${session.orgId}/campaign-performance/${rowData.strategy_id}`}>
            {cellData}
          </Link>
          {rowData.strategy_is_evergreen && <div className="evergreen-tag">Evergreen</div>}
        </div>
      ),
    },
    {
      dataKey: 'strategy_group_name',
      label: 'Budget Name',
      width: 350,
      flexWidth: true,
      cellRender: ({ cellData }) => <div style={{ whiteSpace: 'normal' }}>{cellData}</div>,
      isShowing: !strategyGroupFilterId,
    },
    {
      dataKey: 'strategy_type',
      label: 'Objective',
      cellRender: ({ cellData }) => {
        if (!cellData) return '-';
        return <div style={{ whiteSpace: 'normal' }}>{STRATEGY_OBJECTIVES[cellData]}</div>;
      },
    },
    {
      dataKey: 'strategy_roi_goal',
      label: 'ROI Goal',
      width: 100,
      flexWidth: true,
      justifyContent: 'right',
      format: { options: { precision: 1 } },
      cellRender: ({ cellData }) => {
        if (typeof cellData === 'undefined' || cellData === null) return '-';
        return <div style={{ whiteSpace: 'normal' }}>{cellData}</div>;
      },
    },
    {
      dataKey: 'roi',
      label: 'ROI Actual',
      width: 100,
      flexWidth: true,
      justifyContent: 'right',
      format: { options: { precision: 1 } },
      cellRender: ({ cellData, rowData }) => roiActualRender(cellData, rowData),
      totalRender: ({ cellData, rowData }) => roiActualRender(cellData, rowData),
    },
    {
      dataKey: 'strategy_budget_amount',
      label: 'Budget Cap',
      justifyContent: 'right',
      format: { type: 'currency', options: { precision: 0 } },
    },
    {
      dataKey: 'merchant_spend',
      label: 'Spend',
      justifyContent: 'right',
      format: { type: 'currency', options: { precision: 0 } },
    },
    {
      dataKey: 'merchant_spend_pacing',
      label: 'Campaign Pacing',
      width: 130,
      justifyContent: 'right',
      format: { type: 'currency', options: { precision: 0 } },
      cellRender: ({ cellData, rowData }) => {
        // TODO: Move this into a generic wrapper function
        if (typeof cellData === 'undefined' || cellData === null) {
          return '-';
        }

        const pacingIndicator = PacingStatus({
          value:
            rowData.merchant_spend && rowData.merchant_spend > 0
              ? parseFloat(rowData.merchant_spend_pacing) / parseFloat(rowData.merchant_spend)
              : 0,
          size: 11,
        });

        return (
          <div className="pacing-cell">
            <div style={{ color: pacingIndicator.props.color }}>
              {formatCellValue(rowData.merchant_spend_pacing, 'currency', {
                options: { precision: 0 },
              })}
            </div>
            {pacingIndicator}
          </div>
        );
      },
    },
    {
      dataKey: 'attributed_revenue',
      label: 'Revenue',
      justifyContent: 'right',
      format: { type: 'currency', options: { precision: 0 } },
    },
    {
      dataKey: 'impressions',
      label: 'Impressions',
      justifyContent: 'right',
      format: { options: { precision: 0 } },
    },
    {
      dataKey: 'clicks',
      label: 'Clicks',
      justifyContent: 'right',
      format: { options: { precision: 0 } },
    },
    {
      dataKey: 'strategy_priority',
      label: 'Priority',
      width: 100,
      justifyContent: 'center',
      cellRender: ({ cellData, rowData }) => {
        if (rowData.strategy_is_evergreen) {
          return '-';
        }

        return cellData;
      },
    },
    {
      dataKey: 'strategy_datetime_start',
      label: 'Start Date',
      justifyContent: 'right',
      format: { type: 'date', options: { dateFormat: 'M/DD/YYYY' } },
      width: 150,
      cellRender: ({ cellData }) => {
        if (!cellData) return '-';
        const daysPassed = Math.floor(
          dateService.subtractDatesV2(
            dateService.getStartOfDay(moment()),
            moment(cellData).utc(),
            'day',
          ) - 1,
        );

        return (
          <div className="date-info-cell">
            <div>{cellData}</div>
            <div>
              {daysPassed >= 0
                ? `${daysPassed} ${pluralize('day', daysPassed)} passed`
                : 'Not Started Yet'}
            </div>
          </div>
        );
      },
    },
    {
      dataKey: 'strategy_datetime_end',
      label: 'End Date',
      width: 150,
      justifyContent: 'right',
      format: { type: 'date', options: { dateFormat: 'M/DD/YYYY' } },
      cellRender: ({ cellData }) => cellData || 'No End Date',
    },
  ];

  //  Table Data
  useEffect(() => {
    setSelectedColumns(
      columns.map(col => {
        return { label: col.label, value: col.dataKey };
      }),
    );
    setLoading(true);
    const tableEndpoint = `/api/v0/merchants/${merchantId}/strategies/overview/`;
    const totalsEndpoint = `/api/v0/merchants/${merchantId}/strategies/aggregated_overview/`;
    const params = getParams();
    const promises = [api.get(tableEndpoint, params), api.get(totalsEndpoint, params)];

    Promise.all(promises)
      .then(resp => {
        const tableData = resp[0].data.data[0];
        const totalsData = resp[1].data.data[0];

        setData(tableData);
        setTotals(totalsData);
        setLoading(false);
      })
      .catch(e => console.log(e));
    setUpdatedCampaignStatus(false);
  }, [
    searchQuery,
    page,
    perPage,
    statusFilter,
    labelFilter,
    selectedPubs,
    strategyGroupFilterId,
    updatedCampaignStatus,
    selectedObjective,
    dateRange,
  ]);

  //  Campaign Select Options
  useEffect(() => {
    const endpoint = `/api/v0/merchants/${merchantId}/campaigns/overview/`;
    api.get(endpoint).then(resp => {
      const data = resp.data.data[0].strategy_groups;
      const options = data.map(strategyGroup => ({
        label: strategyGroup.strategy_group_name,
        value: strategyGroup.strategy_group_id,
      }));

      setCampaignOptions(options);
    });
  }, []);

  const getParams = () => {
    const params = {
      page,
      per_page: perPage,
      status: statusFilter.length ? statusFilter.map(f => f.value).join(',') : null,
      labels: labelFilter.length ? labelFilter.map(f => f.value).join(',') : null,
      pub_ids: selectedPubs.length ? selectedPubs.map(p => p.value).join(',') : null,
      strategy_types: selectedObjective.length
        ? selectedObjective.map(o => o.value).join(',')
        : null,
      filters: buildQueryFilter([
        {
          field: 'strategy_name',
          operation: 'contains',
          value: searchQuery,
        },
      ]),
      strategy_group_ids: strategyGroupFilterId,
      date_from: dateRange.startDate.format('YYYY-MM-DD'),
      date_to: dateRange.endDate.format('YYYY-MM-DD'),
    };

    return params;
  };

  const updateStrategyStatus = (strategyId, updatedStatus) => {
    api.put(`/api/v0/merchants/${merchantId}/strategies/${strategyId}/update_strategy/`, {
      strategy: { strategy_is_enabled: updatedStatus === 'unpause' },
    });
    setUpdatedCampaignStatus(true);
  };

  const onOverflowSelect = (action, strategyGroupId, strategyId) => {
    switch (action) {
      case 'edit':
        history.push(
          `/v2/merchant/${merchantId}/campaign-center/budgets/${strategyGroupId}/campaigns/${strategyId}/edit/`,
        );
        break;
      case 'pause':
        updateStrategyStatus(strategyId, 'pause');
        break;
      case 'unpause':
        updateStrategyStatus(strategyId, 'unpause');
        break;
      default:
        console.err('invalid action');
        break;
    }
  };

  const setDates = (startDate, endDate) => {
    setDateRange({
      startDate: dateService.dateFromStr(startDate),
      endDate: dateService.dateFromStr(endDate),
    });
  };

  const CountLabel = count => {
    if (typeof count === 'undefined' || count === null) {
      return null;
    }

    let label = buildCountDescription(count, 'Campaign', statusFilter);

    if (strategyGroupFilterId && topBarData) {
      label += ` for "${topBarData.strategy_group_name}"`;
    }

    return label;
  };

  const SummaryBar = ({ data }) => {
    if (!data) return null;
    const pacingRatio = parseFloat(data.merchant_spend_pacing) / parseFloat(data.merchant_spend);

    if (strategyGroupFilterId) {
      const daysRemaining =
        data.strategy_group_status === 1 &&
        data.strategy_group_datetime_end &&
        Math.floor(
          dateService.subtractDates(moment(data.strategy_group_datetime_end), moment(), 'day') - 1,
        );

      return (
        <div className="summary-info">
          <div className="stat">
            <StrategyStatus status={data.strategy_group_status} enumKeys={true} styleType="text" />
            {daysRemaining ? <span>{` (${daysRemaining} days left)`}</span> : null}
          </div>
          <div className="stat">
            <span className="divider">|</span>
            <span>Total Budget (as of today): </span>
            <span className="bold">
              ${formatNumber(data.strategy_group_budget_amount, 'currency')}
            </span>
          </div>
          <span className="divider">|</span>
          <div className="stat">
            <span>Total Spend (today): </span>
            <span className="bold">${formatNumber(data.merchant_spend, 'currency')} </span>
            {data.merchant_spend_pacing !== null && data.merchant_spend_pacing !== undefined ? (
              <span>
                <span>Pacing towards: </span>
                <span className="pacing">
                  <span className="bold">
                    ${formatNumber(data.merchant_spend_pacing, 'currency')}
                  </span>
                  <div className="pacing-icon">
                    <PacingStatus value={pacingRatio} />
                  </div>
                </span>
              </span>
            ) : null}
          </div>
          <span className="divider">|</span>
          <div className="stat">
            {moment(data.strategy_group_datetime_start).format('MMM D, YYYY')}
            {data.strategy_group_datetime_end
              ? ` - ${moment(data.strategy_group_datetime_end).format('MMM D, YYYY')}`
              : null}
          </div>
          <span className="divider">|</span>
          <div className="stat">
            <span>
              <span className="bold">{`${data.merch_num_live_strategies} `}</span>
              <span>Live, </span>
            </span>
            <span>
              <span className="bold">{`${data.merch_num_upcoming_strategies} `}</span>
              <span>Upcoming Campaigns</span>
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="summary-info">
        <div className="stat">
          <span>Total Budget (this month): </span>
          <span className="bold">{`$${formatNumber(data.merch_monthly_budget, 'currency')}`}</span>
        </div>
        <span className="divider">|</span>
        <div className="stat">
          <span>Total Spend (this month): </span>
          <span className="bold">{`$${formatNumber(data.merchant_spend, 'currency')} `}</span>
          <span>Pacing towards: </span>
          <span className="pacing">
            <span className="bold">${formatNumber(data.merchant_spend_pacing, 'currency')}</span>
            <div className="pacing-icon">
              <PacingStatus value={pacingRatio} />
            </div>
          </span>
        </div>
        <span className="divider">|</span>
        <div className="stat">
          <span>
            <span className="bold">{`${data.merch_num_live_strategies} `}</span>
            <span>Live, </span>
          </span>
          <span>
            <span className="bold">{`${data.merch_num_upcoming_strategies} `}</span>
            <span>Upcoming Campaign{data.merch_num_upcoming_strategies === 1 ? '' : 's'}</span>
          </span>
        </div>
      </div>
    );
  };

  const handleCreateStrategy = e => {
    if (strategyGroupFilterId) {
      history.push(`budgets/${strategyGroupFilterId}/campaigns/new`);
    } else {
      setCampaignSelectOpen(true);
    }
  };

  const CampaignsSelectModal = ({ isShowing }) => {
    if (!isShowing) return null;
    return (
      <ModalWrapper
        flexHeight={true}
        //  Dropdown events do not place nicely with useClickOutside hook, need empty event handler
        closeModal={() => {}}
      >
        <div className="campaigns-select-modal">
          <div className="modal-header">
            <div className="header-label">Select Existing Budget</div>
            <div className="header-description">
              Which Budget would you like to create this Campaign for?
            </div>
          </div>
          <div className="modal-body">
            <InputWrapper label="Budget">
              <DropdownSelect
                styleType="bordered"
                items={campaignOptions}
                onSelect={setNewStrategyCampaign}
                label={newStrategyCampaign ? newStrategyCampaign.label : 'Select Budget'}
                matchToggleWidth
              />
            </InputWrapper>
          </div>
          <div className="modal-footer">
            <Button
              label="Cancel"
              kind="text"
              size="large"
              onClick={() => setCampaignSelectOpen(false)}
            />
            <Button
              label="Create"
              size="large"
              disabled={!newStrategyCampaign}
              onClick={() => history.push(`budgets/${newStrategyCampaign.value}/campaigns/new`)}
            />
          </div>
        </div>
      </ModalWrapper>
    );
  };

  const TopLevelActionBar = () => {
    return (
      <div className="top-level-actions">
        <div className="left">
          <Button
            iconSize={12}
            iconStart="plus"
            label="Create Campaign"
            onClick={handleCreateStrategy}
          />
          <div className="search-container">
            <Search placeholder="Search For a Campaign" onSearch={setSearchQuery} debounce={true} />
          </div>
        </div>
        <div className="right">
          <DatePicker
            eventPrefix="Campaign Center Campaigns Tab Date Picker"
            setDateRange={setDates}
            dateRange={dateRange}
            allowFutureDates={false}
            futureLimit={dateService.getCurrentDateTime()}
          />
        </div>
      </div>
    );
  };

  const getStatusFilterLabel = () => {
    if (!statusFilter.length || statusFilter.length === statusDropdownItems.length) {
      return 'All Statuses';
    }

    if (statusFilter.length === 1) {
      return statusFilter[0].label;
    }

    return `${statusFilter.length} Statuses`;
  };

  const TableLevelActionBar = data => {
    return (
      <div className="table-level-actions">
        <div className="left">
          <div className="stats-count">
            {CountLabel(data && data.page_info && data.page_info.total_items)}
          </div>
        </div>
        <div className="right">
          <PublisherDropdown
            selected={selectedPubs}
            onSelect={setSelectedPubs}
            confirmPrompt={true}
          />
          <DropdownSelect
            onSelect={setStatusFilter}
            selected={statusFilter}
            multi={true}
            confirmPrompt={true}
            label={getStatusFilterLabel()}
            items={statusDropdownItems}
          />
          <DropdownSelect
            onSelect={setSelectedObjective}
            selected={selectedObjective}
            multi={true}
            label={
              !selectedObjective.length
                ? 'Objective'
                : selectedObjective.length > 1
                ? `${selectedObjective.length} Objectives`
                : `${selectedObjective[0].label}`
            }
            confirmPrompt={true}
            items={Object.keys(STRATEGY_OBJECTIVES).map(key => {
              return { label: STRATEGY_OBJECTIVES[key], value: key };
            })}
          />
          <DropdownSelect
            onSelect={setSelectedColumns}
            selected={selectedColumns}
            multi={true}
            alternateIcon="columns"
            iconSize={14}
            confirmPrompt={true}
            items={columns
              .filter(col => col.label)
              .map(col => {
                return { label: col.label, value: col.dataKey };
              })}
          />
          <ReportDropdown
            statType={statType}
            orgId={merchantId}
            gaEvent={{
              category: `Campaign Center Strategies Tab`,
              label: 'Export Data',
            }}
            queryParams={getParams()}
            columns={selectedColumns
              .filter(col => col.label)
              .map(col => {
                return { selected: true, name: col.value };
              })}
            currentPage={page}
            perPage={perPage}
            rowCounts={{
              visible: data.stats && data.stats.length,
              all: data.page_info && data.page_info.total_items,
            }}
            disabled={loading}
            reportVersion="V2"
            useDarkIcon={true}
          />
        </div>
      </div>
    );
  };

  const StatTiles = () => {
    return (
      <MerchantStrategyTotals
        merchId={merchantId}
        statType={statType}
        dateRange={dateRange}
        hideTargetPacing={true}
      />
    );
  };

  const StrategiesTable = ({ loading, data }) => {
    let tableData;
    if (loading || !data) {
      tableData = <Loader loading={true} />;
    } else if (data.stats.length === 0) {
      if (strategyGroupFilterId && !isFiltered) {
        tableData = (
          <div className="no-strategies">
            <div className="label">No Campaigns have been created for this Budget.</div>
            <Link to={`budgets/${strategyGroupFilterId}/campaigns/new`}>
              <Button iconStart="plus" label="Create Campaign" size="large" />
            </Link>
          </div>
        );
      } else {
        tableData = <NoResult noResult={true} type="campaign-center-campaigns-table" />;
      }
    } else {
      tableData = (
        <Table
          rowIdField="strategy_id"
          data={data.stats}
          visibleColumns={new Set(selectedColumns.map(col => col.value))}
          totals={totals.stats}
        >
          {columns.map((col, idx) => {
            return (
              <Column
                key={col.key || idx}
                dataKey={col.dataKey}
                label={col.label}
                format={col.format}
                justifyContent={col.justifyContent}
                flexWidth={col.flexWidth}
                width={col.width}
                cellRender={col.cellRender}
                totalRender={col.totalRender}
              />
            );
          })}
        </Table>
      );
    }

    const paginationData = data.page_info ? (
      <div className="pager">
        <Pagination
          showPagination={true}
          currentPage={page}
          totalItems={data.page_info.total_items}
          perPage={perPage}
          updatePage={val => setPage(val)}
          updatePerPage={val => {
            setPerPage(val);
            setPage(1);
          }}
          reducedPagination={true}
        />
      </div>
    ) : null;

    return (
      <div className="stats-container">
        {TableLevelActionBar(data)}
        {tableData}
        {paginationData}
      </div>
    );
  };

  const isFiltered =
    !!searchQuery || labelFilter.length || statusFilter.length || selectedObjective.length;

  const snackBar = snackbarTxt ? (
    <SnackBar message={snackbarTxt} setSnackbarTxt={setSnackbarTxt} timeout={5} />
  ) : null;

  return (
    <div className="campaign-center-tab">
      {SummaryBar({ data: topBarData })}
      {TopLevelActionBar()}
      {StatTiles()}
      {StrategiesTable({ data, loading })}
      {CampaignsSelectModal({ isShowing: isCampaignSelectOpen })}
      {snackBar}
    </div>
  );
};

StrategiesTab.propTypes = {};

StrategiesTab.defaultProps = {};

export default StrategiesTab;
